import useAnimal from "common/hooks/useAnimal";
import { Animal } from "types/Animal";
import ImageComponent from "common/components/ImageComponent/ImageComponent";
import ImageFromStrapiMedia from "common/components/ImageFromStrapiMedia/ImageFromStrapiMedia";
import { useTranslation } from "react-i18next";
import Button from "common/components/Button/Button";
import getAnimalLabel from "utils/animalLabel";
import useAuth from "common/hooks/useAuth";
import { Link } from "react-router-dom";
import clsx from "clsx";
import AnimalSelect, { AnimalSelectProps } from "./AnimalSelect";

type Props = {
  values: { animals: number[] };
  form: any;
  disabled?: boolean;
} & AnimalSelectProps;

function AnimalSelectSection({ values, form, ...props }: Props) {
  const { t } = useTranslation();
  const { animals } = useAnimal();
  const { user: currentUser } = useAuth();

  const canSeeDetailsFromItem =
    (currentUser?.isSuperadmin ||
      currentUser?.isAdmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.animal?.read)) ??
    false;

  const removeAnimal = (deleteId: number) => {
    const newAnimals = values.animals.filter(
      (animalId: number) => animalId !== deleteId
    );
    form.mutators.setFieldValue({
      field: "animals",
      value: newAnimals,
    });
  };

  return (
    <div>
      <div className="flex-1 w-full">
        <AnimalSelect multiple {...props} />
      </div>

      {values?.animals?.map((animalId: number) => {
        const animal = animals?.find(
          (a: Animal) => a.id.toString() === animalId.toString()
        );

        const editLink = `/animals/edit/${animalId}`;

        return (
          <div className="responsive-flex gap-4 items-center justify-start border bg-slate-50 border-slate-200 p-3 rounded-lg text-slate-700 my-4">
            <Link
              to={editLink}
              className={clsx(
                "flex w-full gap-4",
                !canSeeDetailsFromItem && "pointer-events-none"
              )}
            >
              {/* very left */}
              <div className="flex relative shrink-0 mb-2 lg:mb-0 w-16 max-w-[4rem] h-16">
                <div className="rounded-lg w-full h-full overflow-hidden  lg:w-auto">
                  <ImageComponent
                    image={
                      animal?.picture
                        ? ImageFromStrapiMedia(animal?.picture).uri
                        : undefined
                    }
                  />
                </div>
              </div>

              {/* middle */}
              <div className="flex flex-col xl:flex-row w-full">
                <div className="flex flex-col flex-1">
                  <div className="flex flex-row align-center gap-3">
                    <h4 className="text-xl font-bold text-primary">
                      {getAnimalLabel(
                        animal?.id,
                        animal?.houseName,
                        animal?.zimsId,
                        animal?.localId,
                        animal?.tag
                      )}
                    </h4>
                    <div className="px-2 rounded-md bg-green-100 text-green-800 border border-green-500 flex flex-col justify-center items-center text-sm w-fit self-start">
                      {t(`animals.${animal?.gender}`)}
                    </div>
                  </div>
                  <div className="flex flex-col flex-1 justify-start mt-1">
                    {animal?.species &&
                    animal?.species.scientificName &&
                    animal?.species.name ? (
                      <div className="flex flex-row items-center text-sm gap-1">
                        <span className="font-bold">
                          {animal?.species.scientificName}
                        </span>
                        <span className="text-gray-400 italic">{`(${animal?.species.name})`}</span>
                      </div>
                    ) : animal?.species ? (
                      <div className="flex flex-row items-center text-sm">
                        <span className="font-bold">
                          {animal?.species.scientificName ??
                            animal?.species.name}
                        </span>
                      </div>
                    ) : null}
                  </div>

                  {animal?.zimsId && (
                    <div className="flex items-center gap-1 text-sm">
                      <span className="font-bold">{`ID: ${animal?.zimsId}`}</span>
                    </div>
                  )}
                </div>
              </div>
            </Link>

            <div className="w-full md:w-auto flex justify-end">
              <Button
                type="error-line"
                onClick={() => removeAnimal(animalId)}
                icon="TrashIcon"
                className="px-3 py-3"
                compact
                disabled={props?.disabled ?? false}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default AnimalSelectSection;
